<template>
	<v-select ref="select" v-bind="currentConfig" @open="onOpen" @close="onClose" @search="onSearch">
		<template v-for="(slot, slotName) in slots" #[slotName]="slotProps">
			<slot :name="slotName" v-bind="slotProps" />
		</template>

		<template #no-options="{ search, searching, loading }">
			<span v-show="!loadingData">Sorry, no matching options.</span>
		</template>

		<template #search="{ attributes, events }">
			<input class="vs__search" :required="isRequired && required" v-bind="attributes" v-on="events" />
		</template>

		<template #list-footer>
			<li v-if="url !== undefined" v-show="(hasMoreData ?? false) || loadingData" ref="intersect" class="loader">
				<div class="flex justify-between px-4">
					<span>Loading More</span>

					<Spinner width-class="w-[32px]" />
				</div>
			</li>
		</template>
	</v-select>
</template>

<script setup>
import { computed, nextTick, reactive, ref, useSlots, useAttrs, watch } from "vue";
import VSelect from "vue-select/dist/vue-select";
import "vue-select/dist/vue-select.css";
import useAxios from "../../../../helpers/useAxios.js";
import useIntersectionObserver from "../../../../helpers/useIntersectionObserver.js";
import urlProcessor from "../../../../helpers/urlProcessor.js";
import Spinner from "../../../loading/Spinner.vue";
import debounce from "../../../../helpers/debounce.js";

const props = defineProps({
	options    : Array,
	url        : String,
	required   : Boolean,
	config     : {
		type    : Object,
		default : () => ({}),
	},
	onDataReceived: {
		type    : Function,
		default : data => data 
	}
});

const attrs = useAttrs();
const slots = useSlots();

const loadingData = ref(false)

const select = ref(null)

//Triggered when the dropdown is opened
const onOpen = () => {
	if (hasMoreData.value) {
		onStartObserving();
	}
};

//Triggered when the dropdown is closed
const onClose = () => {
	onStopObserving();
};

//Triggered when the dropdown is searched
const onSearch = (term) => {
	debounce(() => {
		if (cancellationController !== null) {
			cancellationController.value.abort();
		}

		parameters.page = 1;
		parameters.search = term;

		allData.value = [];
	})();
};

/**
 * Definitions
 */

//The data to render with
const allData = ref(props.options ?? []);

//Pagination Data
const currentUrl = ref(props.url); //The url we previously sent a request to for the next set of data

const hasMoreData = ref(true); //If the api has more pages to provide
const parameters = reactive({
	page: 0, //Start at 0 so when we increment the current page the first request is made to page 1
	search: null,
});

//Intersection Data
let intersect = null;
let onStartObserving = () => {};
let onStopObserving = () => {};

//Used to abort axios requests
let cancellationController = null;

//if we were given a url we should initialise axios to handle our requests
if (currentUrl.value) {
	//we have a url here so we need to start the intersection observer
	//so we can follow the loading element to make requests
	({ intersect, onStartObserving, onStopObserving } = useIntersectionObserver(([{ isIntersecting, target }]) => {
		if (isIntersecting) {
			const ul = target.offsetParent;
			const scrollTop = target.offsetParent.scrollTop;

			//Even if we're intersecting if we have an active request we should wait before loading a new page
			if (cancellationController !== null) {
				parameters.page += 1;
			}


			nextTick(() => {
				ul.scrollTop = scrollTop;
			});
		}
	}));

	let data = []; //Without the semi colon here an error is thrown
	let apiLoading = false;

	//We only need the data from axios to display in the select
	//using the loading prop and assigning it to the dropdown makes it lose the observer
	({ data, cancellationController, loading: apiLoading } = useAxios(currentUrl, { immediate: false }));

	watch(apiLoading, (isApiLoading) => {
		loadingData.value = isApiLoading
	})

	//If the parameters change we should update the url axios is referencing
	watch(parameters, (newData) => {
		currentUrl.value = urlProcessor(props.url, newData);
	});

	//If the data has changed axios has got another page so we should append it to the data we're rendering
	watch(data, (newData) => {
		newData = props.onDataReceived(newData)

		hasMoreData.value = !!newData?.links?.next;

		allData.value = [...allData.value, ...newData.data];
	});
	
	/**
	 * When changing the URL we had an issue where it would load the first page twice
	 * and if we change the url the request is automatically made in useAxios (for the most part)
	 * 
	 * So the way to combat this is to cancel the request being made instantly if the url is changed
	 * and allow the request to be made automatically if the select is opened
	 */
	watch(() => props.url, (newUrl) => {
		allData.value = []
		currentUrl.value = newUrl
		hasMoreData.value = true //We need to reload the first page so we should assume we have data to load
		parameters.page = 0

		//allows this to happen after the request has started and thus when the controller should exist
		nextTick(() => {
			if (cancellationController !== null) {
				cancellationController.value.abort();
			}
		})
	})
}

const currentConfig = computed(() => {
	return {
		label: "label",
		options: props.options ?? allData.value ?? [],
		filterable: props.url === undefined, //if we have the url we're filtering ourselves
		...(props.config ?? {}),
	};
});

const isRequired = computed(() => {
	return empty(attrs["model-value"]);
});

const empty = function (value) {
	const emptyValues = [undefined, null, ""];

	for (let index = 0; index < emptyValues.length; index++) {
		if (value === emptyValues[index]) {
			return true;
		}
	}

	if (value instanceof Object) {
		return Object.values(value).length === 0
	}

	return false;
};

defineExpose({
	select: select
})
</script>
